import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UsersIcon,
  UserPlusIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { auditAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { hasPermission } = useAuth();

  const getActionText = (action) => {
    const actions = {
      'CREATE': 'creó el usuario',
      'UPDATE': 'actualizó el usuario',
      'DELETE': 'eliminó el usuario',
      'ACTIVATE': 'activó el usuario',
      'DEACTIVATE': 'desactivó el usuario',
      'CHANGE_EMAIL': 'cambió el email del usuario',
    };
    return actions[action] || action.toLowerCase();
  };

  // Fetch recent activity
  const { data: recentActivity } = useQuery({
    queryKey: ['audit', 'recent'],
    queryFn: () => auditAPI.getRecentActivity(5),
    enabled: hasPermission('audit:read'),
  });

  // Fetch audit stats
  const { data: auditStats } = useQuery({
    queryKey: ['audit', 'stats'],
    queryFn: () => auditAPI.getAuditStats(),
    enabled: hasPermission('audit:read'),
  });

  const stats = [
    {
      name: 'Total de Usuarios',
      value: auditStats?.data?.totalUsers || '0',
      icon: UsersIcon,
      color: 'bg-blue-500',
      show: hasPermission('user:read'),
    },
    {
      name: 'Usuarios Activos',
      value: auditStats?.data?.activeUsers || '0',
      icon: UserPlusIcon,
      color: 'bg-green-500',
      show: hasPermission('user:read'),
    },
    {
      name: 'Acciones Recientes',
      value: auditStats?.data?.actionsLast24h || '0',
      icon: ClockIcon,
      color: 'bg-yellow-500',
      show: hasPermission('audit:read'),
    },
    {
      name: 'Total de Acciones',
      value: auditStats?.data?.totalActions || '0',
      icon: ChartBarIcon,
      color: 'bg-purple-500',
      show: hasPermission('audit:read'),
    },
  ].filter((stat) => stat.show);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Control</h1>
          <p className="mt-1 text-sm text-gray-500">
            Bienvenido al Sistema de Gestión de Usuarios B2C
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <div
              key={stat.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-2xl font-semibold text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Acciones Rápidas
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {hasPermission('user:read') && (
                <Link
                  to="/users"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                >
                  <div className="flex-shrink-0">
                    <UsersIcon className="h-10 w-10 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Ver Usuarios</p>
                    <p className="text-sm text-gray-500">Navegar todos los usuarios B2C</p>
                  </div>
                </Link>
              )}

              {hasPermission('user:create') && (
                <Link
                  to="/users/new"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                >
                  <div className="flex-shrink-0">
                    <UserPlusIcon className="h-10 w-10 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Crear Usuario</p>
                    <p className="text-sm text-gray-500">Agregar un nuevo usuario B2C</p>
                  </div>
                </Link>
              )}

              {hasPermission('audit:read') && (
                <Link
                  to="/audit"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                >
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-10 w-10 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Historial de Auditoría</p>
                    <p className="text-sm text-gray-500">Ver registros de actividad</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        {hasPermission('audit:read') && recentActivity?.data?.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Actividad Reciente
              </h3>
              <div className="flow-root">
                <ul className="-mb-8">
                  {recentActivity.data.map((activity, idx) => (
                    <li key={activity.auditId}>
                      <div className="relative pb-8">
                        {idx !== recentActivity.data.length - 1 && (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        )}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                              <ClockIcon className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                            <div>
                              <p className="text-sm text-gray-900">
                                <span className="font-medium">{activity.performedByName}</span>{' '}
                                {getActionText(activity.action)}{' '}
                                <span className="font-medium">{activity.targetUserName}</span>
                              </p>
                              {activity.fieldChanged && (
                                <p className="mt-0.5 text-xs text-gray-500">
                                  Cambió: {activity.fieldChanged}
                                </p>
                              )}
                            </div>
                            <div className="whitespace-nowrap text-right text-sm text-gray-500">
                              {new Date(activity.performedAt).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                <Link
                  to="/audit"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Ver toda la actividad →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
